import * as RX from "rxjs";
import { ApiService } from "src/js/services/api-service";
import { DeviceSpecific } from "src/js/models/deviceSpecific";
import { onResize$ } from "../../../helpers/onResize";
import { Paginator } from "../../paginator/paginator.component";
import { Failure } from "src/js/helpers/failure";
import Handlebars from "handlebars";
import { toInteger } from "lodash-es";
import { UGElement, ElementListWithDataset } from "../../../models/module";
import { ModuleListEvent, ModuleListEventClickSingle } from "../moduleListView";
import morphdom from "morphdom";

import emptyListTemplate from "./emptyList.handlebars";
import { IQueryService } from "src/js/services/query-service";
import { hide, show } from "src/js/helpers/dom-element-helpers";
import {
  setPostTypeForTemplate,
  setTemplateType,
} from "src/js/helpers/element-helpers";

type PageChangeResult<T> = {
  kind: "PageChange";
  items: T[];
  pageData: { pageNumber: number };
};

type DataChangeResult<T> = {
  kind: "DataChange";
  items: T[];
  pageData: { pageNumber: number };
};

type NoPaginationResult<T> = {
  kind: "NoPagination";
  items: T[];
};

type PaginationResult<T> =
  | PageChangeResult<T>
  | DataChangeResult<T>
  | NoPaginationResult<T>;

const renderEmptyList = Handlebars.compile(emptyListTemplate);

export type PaginationParameters = {
  pageSize: DeviceSpecific<number>;
  paginationContainers: HTMLElement[];
  elementsContainer: HTMLElement;
  queryService: IQueryService;
  apiService: ApiService;
};

export class PaginatedView {
  public readonly event$: RX.Observable<ModuleListEvent>;
  public readonly render$: RX.OperatorFunction<
    ElementListWithDataset,
    ElementListWithDataset
  >;
  private constructor(
    event$: RX.Observable<ModuleListEvent>,
    render$: RX.OperatorFunction<ElementListWithDataset, ElementListWithDataset>
  ) {
    this.event$ = event$;
    this.render$ = render$;
  }

  static MakeView(config: PaginationParameters) {
    const render$ = this.Render(
      config.elementsContainer,
      config.paginationContainers,
      config.pageSize,
      config.queryService
    );
    const event$ = this.Events(config.elementsContainer).pipe(
      RX.tap({
        subscribe: () => config.paginationContainers.forEach(show),
        unsubscribe: () => {
          config.paginationContainers.forEach(hide);
          config.elementsContainer.innerHTML = "";
        },
      })
    );
    return new PaginatedView(event$, render$);
  }

  static Events(
    elementsContainerElement: HTMLElement
  ): RX.Observable<ModuleListEvent> {
    const itemClickEvent$ = getItemClickEvents$(elementsContainerElement).pipe(
      RX.map((clickedElement) => {
        const postIdStr =
          clickedElement.dataset.id ??
          Failure("Post is missing a data-id property");
        const postId =
          toInteger(postIdStr) ?? Failure("PostId must be integer");

        const event: ModuleListEventClickSingle = {
          type: "clickSingle",
          moduleId: postId,
          target: clickedElement,
        };
        return event;
      })
    );
    return itemClickEvent$;
  }

  static Render(
    elementsContainerElement: HTMLElement,
    paginationContainers: HTMLElement[],
    pageSize: DeviceSpecific<number>,
    queryService: IQueryService
  ): RX.OperatorFunction<ElementListWithDataset, ElementListWithDataset> {
    return (postsWithDataset$: RX.Observable<ElementListWithDataset>) => {
      const posts$ = postsWithDataset$.pipe(
        RX.map((postsWithDataset) => postsWithDataset.elements)
      );

      const postsOnCurrentPage$: RX.Observable<PaginationResult<UGElement>> =
        paginationContainers.length > 0
          ? this.withPagination(
              elementsContainerElement,
              paginationContainers,
              pageSize,
              queryService,
              posts$
            )
          : getPaginationPageSize$(elementsContainerElement, pageSize).pipe(
              RX.switchMap((currentPageSize) =>
                posts$.pipe(
                  RX.map(
                    (posts): NoPaginationResult<UGElement> => ({
                      kind: "NoPagination", // Korrekt type
                      items: posts.slice(0, currentPageSize),
                    })
                  )
                )
              )
            );

      const renderEmpty = () =>
        morphdom(
          elementsContainerElement,
          `<div class="elements">${renderEmptyList({})}</div>`,
          { childrenOnly: true }
        );

      const renderWithItems = (moduleWithDataset: ElementListWithDataset) => {
        const html = `<div class="elements">${renderPosts(
          moduleWithDataset
        )}</div>`;
        morphdom(elementsContainerElement, html, { childrenOnly: true });
        return moduleWithDataset;
      };

      const renderedPosts$ = RX.combineLatest([
        postsOnCurrentPage$.pipe(RX.map((event) => event.items)),
        postsWithDataset$,
      ]).pipe(
        RX.map(
          ([posts, allPostsAndDataset]: [
            UGElement[],
            ElementListWithDataset
          ]) => ({
            elements: posts,
            relatedModules: allPostsAndDataset.relatedModules,
            otherUsersFavorites: allPostsAndDataset.otherUsersFavorites,
            templateOptions: allPostsAndDataset.templateOptions,
          })
        ),
        RX.tap((postsWithDataset: ElementListWithDataset) => {
          if (postsWithDataset.elements.length === 0) {
            renderEmpty();
          } else {
            renderWithItems(postsWithDataset);
          }
        })
      );

      return renderedPosts$;
    };
  }

  private static withPagination(
    elementsContainerElement: HTMLElement,
    paginationContainers: HTMLElement[],
    pageSize: DeviceSpecific<number>,
    queryService: IQueryService,
    posts$: RX.Observable<UGElement[]>
  ): RX.Observable<PaginationResult<UGElement>> {
    const pageCount$ = getPaginationPageSize$(
      elementsContainerElement,
      pageSize
    );
    return Paginator.paginate(paginationContainers, pageCount$, posts$).pipe(
      RX.map((event) => {
        switch (event.kind) {
          case "PageChange":
            return {
              kind: event.kind,
              items: event.pageData.items,
              pageData: { pageNumber: event.pageData.pageNumber },
            };
          case "DataChange":
            return {
              kind: event.kind,
              items: event.pageData.items,
              pageData: { pageNumber: event.pageData.pageNumber },
            };
          default:
            // Dette burde aldrig ske, da PaginatorEvent kun er PageChange eller DataChange
            const _exhaustiveCheck: never = event;
            throw new Error(`Unhandled event kind: ${event}`);
        }
      }),
      RX.tap((mappedEvent) => {
        if (mappedEvent.kind === "PageChange") {
          scrollToTopIfNeeded(elementsContainerElement);
          const pageUrlKey = "_page";
          if (mappedEvent.pageData.pageNumber === 1) {
            queryService.remove(pageUrlKey);
          } else {
            queryService.set(
              pageUrlKey,
              mappedEvent.pageData.pageNumber.toString()
            );
          }
        }
      })
    );
  }
}

function getPaginationPageSize$(
  container: HTMLElement,
  devicesPageSize: DeviceSpecific<number>
) {
  return getElementWidth$(container).pipe(
    RX.map((width) => paginationPageSizeFromModuleWidth(devicesPageSize, width))
  );
}

const ResizeObserverEntryWidth: (entry: ResizeObserverEntry) => number = (
  entry
) => entry.contentBoxSize[0].inlineSize;

const getElementWidth$: (element: HTMLElement) => RX.Observable<number> = (
  element
) =>
  onResize$(element).pipe(
    RX.map(ResizeObserverEntryWidth),
    RX.startWith(element.clientWidth)
  );

function renderPosts(postsWithDataset: ElementListWithDataset): string {
  return postsWithDataset.elements
    .map((item: UGElement) => {
      const templatePostType = setPostTypeForTemplate(item);
      const templateType = setTemplateType(item, "thumbnail");
      return postsWithDataset.relatedModules[templatePostType].templates[
        templateType
      ]({
        element: item,
        relatedModules: postsWithDataset.relatedModules,
        otherUsersFavorites: postsWithDataset.otherUsersFavorites,
        templateOptions: postsWithDataset.templateOptions,
      });
    })
    .join("");
}

function getItemClickEvents$(
  postsContainerElement: HTMLElement
): RX.Observable<HTMLElement> {
  return RX.fromEvent(postsContainerElement, "click").pipe(
    RX.filter(
      // Ignore favorites buttons
      (event) => {
        return !(event.target as HTMLElement).closest(
          ".ug-favorit, .open_element, .open_mapview_modal"
        );
      }
    ),
    RX.map((e) => e.target),
    onlyHtmlElements(),
    RX.filter((element) => element.tagName.toLowerCase() !== "a"), // ignore clicks on links
    RX.map((element) => element.closest<HTMLElement>(".active-link-element")),
    notNull() // ignore elements that are on the container but not a .elementcontainer
  );
}

function onlyHtmlElements(): RX.OperatorFunction<any, HTMLElement> {
  return (x) =>
    x.pipe(
      RX.filter((e) => e instanceof HTMLElement),
      RX.map((e) => e as HTMLElement)
    );
}

function notNull<T>(): RX.OperatorFunction<T | null, T> {
  return (x) =>
    x.pipe(
      RX.filter((e) => e !== null && e !== undefined),
      RX.map((e) => e as T)
    );
}

function paginationPageSizeFromModuleWidth(
  devicesPageSize: DeviceSpecific<number>,
  width: number
): number {
  if (width < 688) {
    return devicesPageSize.mobile;
  } else if (width >= 900 && width < 1200) {
    return devicesPageSize.desktop;
  } else if (width >= 1200) {
    return devicesPageSize.desktopWide;
  } else {
    return devicesPageSize.tablet;
  }
}

function scrollToTopIfNeeded(elm: Element): void {
  function checkTopVisible(elm: Element) {
    var rect = elm.getBoundingClientRect();
    var viewHeight = Math.max(
      // document.documentElement.clientHeight,
      window.innerHeight
    );

    const isElementTopLowerThanWindowTop = rect.top >= 0;
    const isElementTopLowerThanWindowBottom = rect.top <= viewHeight;

    return isElementTopLowerThanWindowTop && isElementTopLowerThanWindowBottom;
  }

  if (!checkTopVisible(elm)) {
    console.log(elm.parentElement);
    elm.parentElement?.scrollIntoView({
      // virker ikke i Safari,
      // behavior: "smooth",
      // block: "start",
      // inline: "start",
    });
  }
}
